// POSInvoices.jsx — v119.141 Invoice list + detail modal (RETM live).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import DateRangePicker, { resolvePreset } from "./DateRangePicker";
import { useAppliedFilters, ApplyButton } from "./lib/applyFilters";
import { zVar } from "./lib/zIndex";

const num = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const money = (v) => `${num(v)} ﷼`;

// جولة تصحيح 2026-10-13 (البند 4/ب) — export كي يُستخدَم من ملف العميل
// (inbox/CustomerCard.jsx) أيضاً. نفس المكوّن بالضبط — لا عرض ثانٍ للفاتورة.
// `client` (بوابة PIN) و`fetchUrl` (مسار بديل بلا بادئة /api) اختياريان؛
// POSInvoices.jsx لا يمرّرهما فيستمر على سلوكه الأصلي بلا أي تغيير.
export function InvoiceModal({ invoiceId, onClose, client, fetchUrl }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const path = fetchUrl || `/pos/invoice/${encodeURIComponent(invoiceId)}`;
        const r = client ? await client.getAbs(`/api${path}`) : await api.get(path);
        setD(r.data);
      } catch { setD(null); }
      finally { setLoading(false); }
    })();
  }, [invoiceId, client, fetchUrl]);

  return (
    <div className="fixed inset-0 z-[var(--z-dropdown)] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="invoice-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto p-5"
           onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-bold text-lg" style={{ color: "#5c3a1e" }}>
            🧾 فاتورة #{d?.invoice_id || invoiceId}
          </h3>
          <button onClick={onClose} className="text-2xl opacity-60 hover:opacity-100">×</button>
        </div>
        {loading && <div className="text-center py-8 opacity-60">جاري التحميل...</div>}
        {!loading && !d && <div className="text-center py-8 text-red-600">لم يتم العثور على الفاتورة</div>}
        {d && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><b>الفرع:</b> {d.branch_name || d.branch_id || "-"}</div>
              <div><b>الجهاز:</b> {d.device_name || d.device_id || "-"}</div>
              <div><b>القناة:</b> {d.order_type_name_resolved || d.channel || "-"}</div>
              <div><b>المرجع:</b> {d.order_reference || "-"}</div>
              <div><b>التاريخ (KSA):</b> {d.created_at_local || "-"}</div>
              <div><b>الحالة:</b> {d.status || "-"}</div>
              <div><b>العميل:</b> {d.customer_name || "زائر"}</div>
              <div><b>جوّال:</b> {d.customer_phone || "—"}</div>
              {/* جولة تصحيح 2026-10-13 (البند 4/ب) — الحقل غير الموجود في
                 المصدر يُعرض «غير مسجّل» صراحة، لا فراغ. */}
              <div data-testid="invoice-modal-cashier">
                <b>الكاشير/الموظف:</b>{" "}
                {d.cashier_name || d.employee_name || d.staff_name
                  || d.cashier || d.employee || "غير مسجّل"}
              </div>
            </div>

            <div className="rounded-lg border p-3" style={{ borderColor: "#eadfc8" }}>
              <div className="font-bold mb-2" style={{ color: "#5c3a1e" }}>الأصناف</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs opacity-70 border-b" style={{ borderColor: "#eadfc8" }}>
                    <th className="text-right py-1">المنتج</th>
                    <th className="text-center">الكمية</th>
                    <th className="text-center">السعر</th>
                    <th className="text-left">الإجمالي</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.products || []).map((p, i) => {
                    const isAddon = /إضاف|sides|add[\s_-]?on/i.test(
                      (p.category_name && (p.category_name.ar || p.category_name.en)) || "");
                    return (
                    <tr key={i} className="border-b last:border-0" style={{ borderColor: "#f5ecdd" }}>
                      <td className="py-1">
                        {(p.name && (p.name.ar || p.name.en)) || p.resolved_name || p.sku || "-"}
                        {isAddon && (
                          <span className="text-[10px] mr-1 px-1.5 py-0.5 rounded-full"
                                style={{ background: "#faf3e6", color: "#8b5a2b" }}>
                            إضافة
                          </span>
                        )}
                      </td>
                      <td className="text-center">{num(p.quantity)}</td>
                      <td className="text-center">{money(p.unit_price)}</td>
                      <td className="text-left">{money(p.total)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg p-2" style={{ background: "#faf3e6" }}>
                <div className="opacity-70 text-xs">قبل الضريبة</div>
                <div className="font-bold">{money(d.subtotal_price)}</div>
              </div>
              <div className="rounded-lg p-2" style={{ background: "#faf3e6" }}>
                <div className="opacity-70 text-xs">الضريبة</div>
                <div className="font-bold">{money(d.tax)}</div>
              </div>
              <div className="rounded-lg p-2" style={{ background: "#faf3e6" }}>
                <div className="opacity-70 text-xs">الخصم{d.discount_id ? ` (كود: ${d.discount_id})` : ""}</div>
                <div className="font-bold">{money(d.discount_amount)}</div>
              </div>
              <div className="rounded-lg p-2" style={{ background: "#5c3a1e", color: "#fff" }}>
                <div className="opacity-80 text-xs">الإجمالي النهائي</div>
                <div className="font-bold text-lg">{money(d.total_price)}</div>
              </div>
            </div>

            {(d.payments || []).length > 0 && (
              <div className="rounded-lg border p-3 text-sm" style={{ borderColor: "#eadfc8" }}>
                <div className="font-bold mb-1" style={{ color: "#5c3a1e" }}>الدفعات</div>
                {(d.payments || []).map((p, i) => (
                  <div key={i} className="flex justify-between border-b last:border-0 py-1"
                       style={{ borderColor: "#f5ecdd" }}>
                    <span>{(p.name && (p.name.ar || p.name.en)) || p.resolved_name || "-"}</span>
                    <span className="font-bold">{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function POSInvoices() {
  const _init = resolvePreset("today");
  const [dateRange, setDateRange] = useState({ from: _init.from, to: _init.to, preset: "today" });
  const [branchId, setBranchId] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [openInv, setOpenInv] = useState(null);
  const [branches, setBranches] = useState([]);

  const { applied, dirty, apply } = useAppliedFilters({ dateRange, branchId, q });
  const applyFilters = () => { setPage(1); apply(); };

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (applied.dateRange.from === applied.dateRange.to) {
      p.set("business_day", applied.dateRange.from);
    } else {
      p.set("month", applied.dateRange.to.slice(0, 7));
    }
    if (applied.branchId) p.set("branch_id", applied.branchId);
    if (applied.q.trim()) p.set("q", applied.q.trim());
    p.set("page", page);
    p.set("limit", "50");
    return p.toString();
  }, [applied, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pos/invoices?${params}`);
      setData(r.data);
    } catch { setData({ items: [], total: 0, pages: 1 }); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/pos/branches"); setBranches(r.data?.branches || []); } catch { /* ignore */ }
    })();
  }, []);

  return (
    <div className="space-y-3" dir="rtl" data-testid="pos-invoices-page">
      <div className="rounded-2xl p-4" style={{ background: "linear-gradient(135deg,#4a2c1a,#8b5a2b)" }}>
        <h1 className="text-white text-xl font-bold">🧾 الفواتير اللحظية</h1>
        <div className="text-white/60 text-xs mt-1">من webhook مباشر RETM</div>
      </div>

      <div className="rounded-2xl border bg-white p-3 flex flex-wrap gap-2 items-center"
           style={{ borderColor: "#eadfc8" }}>
        <DateRangePicker value={dateRange}
                          onChange={setDateRange}
                          testId="inv-date" />
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                className="rounded-full border px-4 py-1.5 text-sm bg-white"
                style={{ borderColor: "#d4a574", color: "#5c3a1e" }}
                data-testid="inv-branch">
          <option value="">كل الفروع</option>
          {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
        </select>
        <input type="text" placeholder="بحث برقم فاتورة/مرجع/جوّال"
               value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && applyFilters()}
               className="rounded-full border px-4 py-1.5 text-sm flex-1 min-w-[200px]"
               style={{ borderColor: "#d4a574" }}
               data-testid="inv-search" />
        <ApplyButton dirty={dirty} onApply={applyFilters} onRefresh={load} testId="inv-apply" />
        <span className="text-xs opacity-60">
          {loading ? "..." : `${num(data.total)} فاتورة · صفحة ${page}/${data.pages}`}
        </span>
      </div>

      <div className="rounded-2xl border bg-white overflow-x-auto" style={{ borderColor: "#eadfc8" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs opacity-70 border-b bg-gray-50" style={{ borderColor: "#eadfc8" }}>
              <th className="text-right px-3 py-2">التاريخ</th>
              <th className="text-right">فاتورة#</th>
              <th className="text-right">الفرع</th>
              <th className="text-right">القناة</th>
              <th className="text-center">الأصناف</th>
              <th className="text-right">العميل</th>
              <th className="text-left px-3">الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((r) => {
              const key = r.invoice_id ?? r.id;
              return (
              <tr key={key}
                  className="border-b hover:bg-yellow-50 cursor-pointer"
                  style={{ borderColor: "#f5ecdd" }}
                  onClick={() => setOpenInv(key)}
                  data-testid={`inv-row-${key}`}>
                <td className="px-3 py-2 text-xs">{(r.created_at_local || "").slice(0, 16)}</td>
                <td className="font-mono text-xs">
                  {r.invoice_id ?? <span className="opacity-60 text-[10px]">📁 تاريخية</span>}
                </td>
                <td>{r.branch_name || "-"}</td>
                <td className="text-xs">{(r.order_type_name && r.order_type_name.ar) || r.channel || "-"}</td>
                <td className="text-center">{num(r.product_count)}</td>
                <td className="text-xs">
                  {r.customer_phone ? r.customer_phone
                    : <span className="opacity-50">زائر</span>}
                </td>
                <td className="text-left px-3 font-bold" style={{ color: "#5c3a1e" }}>
                  {money(r.total_price)}
                </td>
              </tr>
              );
            })}
            {!loading && !data.items?.length && (
              <tr><td colSpan={7} className="text-center opacity-60 py-8">لا فواتير</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex justify-center gap-1 mt-2">
          <button onClick={() => setPage(1)} disabled={page === 1}
                  className="rounded border px-2 py-1 text-xs disabled:opacity-40">«</button>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="rounded border px-2 py-1 text-xs disabled:opacity-40">‹ السابقة</button>
          <span className="px-3 py-1 text-xs">{page} / {data.pages}</span>
          <button onClick={() => setPage((p) => Math.min(data.pages, p + 1))} disabled={page === data.pages}
                  className="rounded border px-2 py-1 text-xs disabled:opacity-40">التالية ›</button>
          <button onClick={() => setPage(data.pages)} disabled={page === data.pages}
                  className="rounded border px-2 py-1 text-xs disabled:opacity-40">»</button>
        </div>
      )}

      {openInv && <InvoiceModal invoiceId={openInv} onClose={() => setOpenInv(null)} />}
    </div>
  );
}
